/**
 * LooseFkDisplayColumnSelect — the "Label column" dropdown rendered inside
 * `LooseRelationshipTargetPicker`, the declare-lookup-enum dialog and the enum
 * details dialog. Persisted at the table level (keyed by `(targetSchema,
 * targetTable)` in `DatabaseMetaInfo.tableDisplayColumns`), so the helper text
 * explicitly flags that this setting is shared by everything that references
 * the target table — real FKs and lookup enums included, not just loose FKs.
 *
 * `optional` reflects how much the *calling* surface needs it: on a loose FK
 * the label column is a nicety (navigation works without it), but in a lookup
 * flow it IS the feature — with no label column there are no names to show —
 * so the "(optional)" suffix is opt-in rather than always on.
 *
 * Kept as a standalone component to keep the parent picker under the
 * project-wide line-count guideline; the parent owns the state machine and
 * the persistence write — this component is the pure render-and-emit surface.
 */

import { SearchableSelect } from '@/components/ui/searchable-select';

// Inlined twice — once here, once in the parent picker that decides what to
// pass for `value` / interpret in `onChange`. Kept un-exported to avoid the
// react-refresh/only-export-components warning the linter raises when a file
// exports anything besides components. The string literal is small enough
// that the duplication is cheaper than the alternative.
const NONE_SENTINEL = '__none__';

export type LooseFkDisplayColumnSelectProps = {
  /** SearchableSelect value: `'__none__'` (explicit none), a column name, or `''` (pristine). */
  value: string;
  onChange: (next: string) => void;
  /** Column options for the target table; the `(none)` sentinel is added internally. */
  columnOptions: ReadonlyArray<{ value: string; label: string }>;
  loading?: boolean;
  /** Used in the helper text to surface what table this entry belongs to. */
  targetSchema: string;
  targetTable: string;
  /**
   * Mark the field "(optional)". True on a loose FK, where a label column is a
   * nicety; false (the default) in the lookup flows, where skipping it means
   * no names at all.
   */
  optional?: boolean;
  /** Portal the dropdown — set inside the bounded (scrollable) editor dialog. */
  portal?: boolean;
};

export function LooseFkDisplayColumnSelect({
  value,
  onChange,
  columnOptions,
  loading,
  targetSchema,
  targetTable,
  optional,
  portal,
}: LooseFkDisplayColumnSelectProps) {
  const options = [
    { value: NONE_SENTINEL, label: '(none)' },
    ...columnOptions,
  ];

  return (
    <div
      className="flex flex-col gap-1"
      data-testid="loose-fk-display-column-row"
    >
      <label
        className="text-[11px] text-muted-foreground"
        htmlFor="loose-fk-display-column"
      >
        Label column
        {optional && (
          <>
            {' '}
            <span className="text-muted-foreground/70">(optional)</span>
          </>
        )}
      </label>
      <SearchableSelect
        value={value}
        onValueChange={onChange}
        options={options}
        placeholder={loading ? 'loading…' : 'pick label column'}
        disabled={loading}
        portal={portal}
        data-testid="loose-fk-display-column"
      />
      <p className="text-[10px] text-muted-foreground/80 leading-snug">
        Stored for{' '}
        <span className="font-mono">
          {targetSchema}.{targetTable}
        </span>{' '}
        — applies to everything that references this table.
      </p>
    </div>
  );
}
